import { ActivityLogEvent, ActivityLogFeature } from 'webapp/enums/activity-log';

export interface ActivityLogEventFilterOption {
	id: ActivityLogEvent;
	text: string;
}

export interface ActivityLogEventFilterGroup {
	feature: ActivityLogFeature;
	events: ActivityLogEventFilterOption[];
}

const ActivityLogEventFilterGroups: ActivityLogEventFilterGroup[] = [
	{
		feature: ActivityLogFeature.BOARDS,
		events: [
			{ id: ActivityLogEvent.BOARD_CREATED, text: 'Dashboard created' },
			{ id: ActivityLogEvent.BOARD_DELETED, text: 'Dashboard deleted' },
			{
				id: ActivityLogEvent.BOARD_PERMANENTLY_DELETED,
				text: 'Dashboard permanently deleted',
			},
			{ id: ActivityLogEvent.BOARD_RESTORED, text: 'Dashboard restored' },
			{ id: ActivityLogEvent.BOARD_PERMISSIONS_CHANGED, text: 'Permissions changed' },
			{ id: ActivityLogEvent.BOARD_OWNER_CHANGED, text: 'Owner changed' },
			{ id: ActivityLogEvent.BOARD_SHARED_EMAIL, text: 'Sent via email' },
			{ id: ActivityLogEvent.BOARD_SHARED_SLACK, text: 'Sent via Slack' },
			{ id: ActivityLogEvent.BOARD_LINK_SHARED, text: 'Shared via link' },
			{ id: ActivityLogEvent.BOARD_SHARED_TO_CLIENT, text: 'Shared to client' },
			{ id: ActivityLogEvent.BOARD_SHARED_TO_AGENCY, text: 'Shared to agency' },
			{ id: ActivityLogEvent.BOARD_DOWNLOADED, text: 'Downloaded' },
			{ id: ActivityLogEvent.BOARD_TEMPLATE_CREATED, text: 'Template created' },
			{ id: ActivityLogEvent.BOARD_TEMPLATE_DELETED, text: 'Template deleted' },
		],
	},
	{
		feature: ActivityLogFeature.LOOPS,
		events: [
			{ id: ActivityLogEvent.BOARD_LOOP_CREATED, text: 'Loop created' },
			{ id: ActivityLogEvent.BOARD_LOOP_DELETED, text: 'Loop deleted' },
			{ id: ActivityLogEvent.BOARD_LOOP_RESTORED, text: 'Loop restored' },
			{ id: ActivityLogEvent.BOARD_LOOP_OWNER_CHANGED, text: 'Owner changed' },
			{ id: ActivityLogEvent.BOARD_LOOP_LINK_SHARED, text: 'Shared via link' },
			{ id: ActivityLogEvent.BOARD_LOOP_SHARED_EMAIL, text: 'Sent via email' },
			{ id: ActivityLogEvent.BOARD_LOOP_SHARED_SLACK, text: 'Sent via Slack' },
			{ id: ActivityLogEvent.BOARD_LOOP_TEMPLATE_CREATED, text: 'Template created' },
			{ id: ActivityLogEvent.BOARD_LOOP_TEMPLATE_DELETED, text: 'Template deleted' },
		],
	},
	{
		feature: ActivityLogFeature.METRICS,
		events: [
			{ id: ActivityLogEvent.METRIC_CUSTOM_QUERY_CREATED, text: 'Custom metric created' },
			{ id: ActivityLogEvent.METRIC_CUSTOM_QUERY_DELETED, text: 'Custom metric deleted' },
			{ id: ActivityLogEvent.METRIC_CALCULATION_CREATED, text: 'Calculated metric created' },
			{ id: ActivityLogEvent.METRIC_CALCULATION_DELETED, text: 'Calculated metric deleted' },
		],
	},
	{
		feature: ActivityLogFeature.REPORTS,
		events: [
			{ id: ActivityLogEvent.REPORT_CREATED, text: 'Report created' },
			{ id: ActivityLogEvent.REPORT_DELETED, text: 'Report deleted' },
			{ id: ActivityLogEvent.REPORT_OWNER_CHANGED, text: 'Owner changed' },
			{ id: ActivityLogEvent.REPORT_DOWNLOADED, text: 'Downloaded' },
			{ id: ActivityLogEvent.REPORT_SHARED_EMAIL, text: 'Sent via email' },
			{ id: ActivityLogEvent.REPORT_SHARED_SLACK, text: 'Sent via Slack' },
			{ id: ActivityLogEvent.REPORT_TEMPLATE_CREATED, text: 'Template created' },
			{ id: ActivityLogEvent.REPORT_TEMPLATE_DELETED, text: 'Template deleted' },
		],
	},
	{
		feature: ActivityLogFeature.DATA_SOURCES,
		events: [
			{ id: ActivityLogEvent.DATA_SOURCE_CREATED, text: 'Data source connected' },
			{ id: ActivityLogEvent.DATA_SOURCE_REAUTHORIZED, text: 'Data source reauthorized' },
			{ id: ActivityLogEvent.DATA_SOURCE_DELETED, text: 'Data source deleted' },
			{ id: ActivityLogEvent.DATA_SOURCE_PERMISSIONS_CHANGED, text: 'Permissions changed' },
			{ id: ActivityLogEvent.DATA_SOURCE_DATA_PURGED, text: 'Data purged' },
			{ id: ActivityLogEvent.DATA_SOURCE_REQUEST_SENT, text: 'Connection request sent' },
			{ id: ActivityLogEvent.DATA_SOURCE_TOKEN_CREATED, text: 'Token created' },
		],
	},
	{
		feature: ActivityLogFeature.GOALS,
		events: [
			{ id: ActivityLogEvent.GOAL_CREATED, text: 'Goal created' },
			{ id: ActivityLogEvent.GOAL_DELETED, text: 'Goal deleted' },
			{ id: ActivityLogEvent.GOAL_OWNER_CHANGED, text: 'Owner changed' },
			{ id: ActivityLogEvent.GOAL_PROGRESS_UPDATED, text: 'Progress update created' },
		],
	},
	{
		feature: ActivityLogFeature.SCORECARDS,
		events: [
			{ id: ActivityLogEvent.SCORECARD_CREATED, text: 'Scorecard created' },
			{ id: ActivityLogEvent.SCORECARD_DELETED, text: 'Scorecard deleted' },
			{ id: ActivityLogEvent.SCORECARD_RECIPIENTS_CHANGED, text: 'Recipients changed' },
		],
	},
	{
		feature: ActivityLogFeature.ALERTS,
		events: [
			{ id: ActivityLogEvent.ALERT_CREATED, text: 'Alert created' },
			{ id: ActivityLogEvent.ALERT_DELETED, text: 'Alert deleted' },
			{ id: ActivityLogEvent.SMART_ALERT_CREATED, text: 'Smart alert created' },
			{ id: ActivityLogEvent.SMART_ALERT_DELETED, text: 'Smart alert deleted' },
		],
	},
	{
		feature: ActivityLogFeature.SNAPSHOTS,
		events: [
			{
				id: ActivityLogEvent.SCHEDULED_SNAPSHOT_RECIPIENTS_CHANGED,
				text: 'Recipients changed',
			},
			{
				id: ActivityLogEvent.SCHEDULED_SNAPSHOT_FREQUENCY_CHANGED,
				text: 'Frequency changed',
			},
			{ id: ActivityLogEvent.SCHEDULED_SNAPSHOT_DELETED, text: 'Snapshot deleted' },
		],
	},
	{
		feature: ActivityLogFeature.GENIE,
		events: [
			{ id: ActivityLogEvent.GENIE_CHAT_LINK_SHARED, text: 'Chat shared via link' },
			{ id: ActivityLogEvent.GENIE_ARTIFACT_LINK_SHARED, text: 'Artifact shared via link' },
			{ id: ActivityLogEvent.GENIE_ARTIFACT_DELETED, text: 'Artifact deleted' },
			{ id: ActivityLogEvent.GENIE_ROUTINE_DELETED, text: 'Routine deleted' },
			{ id: ActivityLogEvent.GENIE_ROUTINE_WEBHOOK_CREATED, text: 'Routine webhook created' },
			{ id: ActivityLogEvent.GENIE_ROUTINE_WEBHOOK_ROTATED, text: 'Routine webhook rotated' },
			{ id: ActivityLogEvent.GENIE_SKILL_DELETED, text: 'Skill deleted' },
			{ id: ActivityLogEvent.GENIE_SKILL_SCOPE_CHANGED, text: 'Skill scope changed' },
			{
				id: ActivityLogEvent.GENIE_MARKETPLACE_SKILL_INSTALLED,
				text: 'Marketplace skill installed',
			},
			{ id: ActivityLogEvent.GENIE_CONNECTOR_CONNECTED, text: 'Connector connected' },
			{ id: ActivityLogEvent.GENIE_CONNECTOR_DISCONNECTED, text: 'Connector disconnected' },
			{ id: ActivityLogEvent.GENIE_CONNECTOR_ADDED, text: 'Custom connector added' },
			{ id: ActivityLogEvent.GENIE_CONNECTOR_REMOVED, text: 'Custom connector removed' },
			{
				id: ActivityLogEvent.GENIE_CONNECTOR_TOOL_PERMISSIONS_CHANGED,
				text: 'Connector tool permissions changed',
			},
			{ id: ActivityLogEvent.GENIE_MEMORY_DELETED, text: 'Memory deleted' },
			{ id: ActivityLogEvent.GENIE_MEMORIES_CLEARED, text: 'All memories deleted' },
		],
	},
	{
		feature: ActivityLogFeature.PLANS_AND_BILLING,
		events: [
			{ id: ActivityLogEvent.TRIAL_STARTED, text: 'Trial started' },
			{ id: ActivityLogEvent.TRIAL_ENDED, text: 'Trial ended' },
			{ id: ActivityLogEvent.STARTED_PAYING_SUBSCRIPTION, text: 'Started paying' },
			{ id: ActivityLogEvent.BILLING_PLAN_CHANGE, text: 'Plan changed' },
			{ id: ActivityLogEvent.BILLING_CYCLE_CHANGE, text: 'Billing cycle changed' },
			{ id: ActivityLogEvent.ADDON_PURCHASED, text: 'Add-on purchased' },
			{ id: ActivityLogEvent.ADDON_REMOVED, text: 'Add-on removed' },
			{ id: ActivityLogEvent.PAYMENT_FAILED, text: 'Payment failed' },
			{
				id: ActivityLogEvent.SUBSCRIPTION_CANCEL_AT_PERIOD_END,
				text: 'Subscription set to cancel',
			},
			{ id: ActivityLogEvent.AI_CREDITS_PURCHASED, text: 'AI credits purchased' },
			{ id: ActivityLogEvent.AI_CREDITS_REMOVED, text: 'AI credits removed' },
			{ id: ActivityLogEvent.CLIENT_PACK_PURCHASED, text: 'Client pack purchased' },
			{ id: ActivityLogEvent.CLIENT_PACK_REMOVED, text: 'Client pack removed' },
		],
	},
	{
		feature: ActivityLogFeature.USERS,
		events: [
			{ id: ActivityLogEvent.USER_DELETED, text: 'User deleted' },
			{ id: ActivityLogEvent.USER_INVITE_REJECTED, text: 'Invite rejected' },
			{ id: ActivityLogEvent.USER_PERMISSION_CHANGED, text: 'Permissions changed' },
			{ id: ActivityLogEvent.USER_ROLE_CHANGED, text: 'Role changed' },
			{ id: ActivityLogEvent.USER_PASSWORD_CHANGED, text: 'Password changed' },
			{ id: ActivityLogEvent.USER_MFA_ENABLED, text: 'MFA enabled' },
			{ id: ActivityLogEvent.USER_MFA_DISABLED, text: 'MFA disabled' },
		],
	},
	{
		feature: ActivityLogFeature.ACCOUNT,
		events: [
			{ id: ActivityLogEvent.ACCOUNT_CLIENT_CREATED, text: 'Client created' },
			{ id: ActivityLogEvent.ACCOUNT_CLIENT_DELETED, text: 'Client deleted' },
			{
				id: ActivityLogEvent.ACCOUNT_CLIENT_PERMISSIONS_CHANGED,
				text: 'Client permissions changed',
			},
			{
				id: ActivityLogEvent.CLIENT_MOVED_TO_PAID_CLIENT,
				text: 'Client moved to paid client',
			},
			{ id: ActivityLogEvent.ACCOUNT_OWNER_CHANGED, text: 'Owner changed' },
			{ id: ActivityLogEvent.ACCOUNT_WHITE_LABELING_CHANGED, text: 'White labeling changed' },
			{ id: ActivityLogEvent.SSO_ENABLED, text: 'SSO enabled' },
			{ id: ActivityLogEvent.FORCE_SSO_ENABLED, text: 'Force SSO enabled' },
			{ id: ActivityLogEvent.FORCE_SSO_DISABLED, text: 'Force SSO disabled' },
			{ id: ActivityLogEvent.FORCE_MFA_ENABLED, text: 'Force MFA enabled' },
			{ id: ActivityLogEvent.FORCE_MFA_DISABLED, text: 'Force MFA disabled' },
			{ id: ActivityLogEvent.USER_API_KEY_CREATED, text: 'API key created' },
			{ id: ActivityLogEvent.USER_API_KEY_REMOVED, text: 'API key removed' },
		],
	},
	{
		feature: ActivityLogFeature.COMPANY_INFORMATION,
		events: [
			{ id: ActivityLogEvent.ACCOUNT_DELETED, text: 'Account deleted' },
			{
				id: ActivityLogEvent.RESELLER_SIGNUP_LINK_DISABLED,
				text: 'Reseller signup link disabled',
			},
		],
	},
];

export default ActivityLogEventFilterGroups;
